<template><div><code-well-header>
  <dt-stack direction="row" gap="400">
    <dt-chip size="sm" :hide-close="true" :interactive="false">Chip</dt-chip>
    <dt-chip size="sm" :interactive="false">Chip</dt-chip>
    <dt-chip size="sm">
      <template #avatar>
        <dt-avatar
          image-src="/assets/images/person.png"
          image-alt="Jaqueline Nackos"
          full-name="Jaqueline Nackos"
        />
      </template>
      <template #default>
        Chip
      </template>
    </dt-chip>
    <dt-chip size="sm">
      <template #icon>
        <dt-icon
          name="phone"
          size="200"
        />
      </template>
      <template #default>
        Chip
      </template>
    </dt-chip>
  </dt-stack>
</code-well-header>
<!-- <component-combinator component-name="DtChip" /> -->
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="non-interactive" tabindex="-1" class="d-docsite--header-3">Non Interactive <a class="header-anchor d-link d-docsite--link d-link" href="#non-interactive" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Add <code v-pre>:interactive=&quot;false&quot;</code> to make it a read-only, non-interactive Chip. This changes it from a <code v-pre>&lt;button&gt;</code> to a non-interactive, read-only Chip with no events or hover/active state. Note that this does not effect the interactivity of its <code v-pre>×</code> remove button.</p>
<code-well-header>
  <dt-chip :interactive="false">Chip</dt-chip>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-chip">
  <span class="d-chip__label">
    <span class="d-chip__text">Chip</span>
  </span>
  <button class="d-chip__close" type="button" aria-label="close">
    <span class="d-btn__icon">
      <svg>...</svg>
    </span>
  </button>
</span>
'
vueCode='
<dt-chip :interactive="false">
  Chip
</dt-chip>
'
showHtmlWarning />
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-chip>Chip</dt-chip>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-chip">
  <button type="button" aria-labelledby="dt0-content" aria-label="" class="d-chip__label">
    <span class="d-chip__text"> Chip </span>
  </button>
  <button type="button" aria-label="close" class="base-button__button d-btn d-btn--primary d-btn--icon-only d-chip__close">
    <span data-qa="dt-button-icon" class="base-button__icon d-btn__icon d-btn__icon--left">
      <svg>...</svg>
    </span>
  </button>
</span>
'
vueCode='
<dt-chip>
  Chip
</dt-chip>
'
showHtmlWarning />
<h3 id="disabled" tabindex="-1" class="d-docsite--header-3">Disabled <a class="header-anchor d-link d-docsite--link d-link" href="#disabled" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use the <code v-pre>disabled</code> prop to disable both the Chip and its close button. This sets <code v-pre>aria-disabled=&quot;true&quot;</code> and <code v-pre>tabindex=&quot;-1&quot;</code> on the interactive elements and applies disabled styles, preventing pointer and keyboard interaction.</p>
<code-well-header>
  <dt-chip disabled ref="example-disabled">Chip</dt-chip>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs['example-disabled']"
vueCode='
<dt-chip disabled>
  Chip
</dt-chip>
'
showHtmlWarning />
<h3 id="without-close-button" tabindex="-1" class="d-docsite--header-3">Without Close Button <a class="header-anchor d-link d-docsite--link d-link" href="#without-close-button" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-chip :hide-close="true">Chip</dt-chip>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-chip">
  <button class="d-chip__label" type="button">
    <span class="d-chip__text">Chip</span>
  </button>
</span>
'
vueCode='
<dt-chip :hide-close="true">
  Chip
</dt-chip>
'
showHtmlWarning />
<h3 id="with-icon" tabindex="-1" class="d-docsite--header-3">With Icon <a class="header-anchor d-link d-docsite--link d-link" href="#with-icon" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-chip with-icon :hide-close="true">
    <template #icon>
      <dt-icon
        name="phone"
        size="200"
      />
    </template>
    <template #default>
      Chip
    </template>
  </dt-chip>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-chip">
  <button class="d-chip__label" type="button">
    <span class="d-chip__icon">
      <svg>...</svg>
    </span>
    <span class="d-chip__text">Chip</span>
  </button>
</span>
'
vueCode='
<dt-chip :hide-close="true">
  <template #icon>
    <dt-icon
      name="phone"
      size="200"
    />
  </template>
  <template #default>
    Chip
  </template>
</dt-chip>
'
showHtmlWarning />
<h3 id="with-icon-and-close-button" tabindex="-1" class="d-docsite--header-3">With Icon and Close Button <a class="header-anchor d-link d-docsite--link d-link" href="#with-icon-and-close-button" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-chip>
    <template #icon>
      <dt-icon
        name="phone"
        size="200"
      />
    </template>
    <template #default>
      Chip
    </template>
  </dt-chip>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-chip">
  <button class="d-chip__label" type="button">
    <span class="d-chip__icon">
      <svg>...</svg>
    </span>
    <span class="d-chip__text">Chip</span>
  </button>
  <button class="d-chip__close" type="button" aria-label="close">
    <span class="d-btn__icon">
      <svg>...</svg>
    </span>
  </button>
</span>
'
vueCode='
<dt-chip>
  <template #icon>
    <dt-icon
      name="phone"
      size="200"
    />
  </template>
  <template #default>
    Chip
  </template>
</dt-chip>
'
showHtmlWarning />
<h3 id="with-avatar-and-close-button" tabindex="-1" class="d-docsite--header-3">With Avatar and Close Button <a class="header-anchor d-link d-docsite--link d-link" href="#with-avatar-and-close-button" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-chip>
    <template #avatar>
      <dt-avatar
        image-src="/assets/images/person.png"
        image-alt="Jaqueline Nackos"
        full-name="Jaqueline Nackos"
      />
    </template>
    <template #default>
      Chip
    </template>
  </dt-chip>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-chip">
  <button class="d-chip__label" type="button">
    <span class="d-avatar">...</span>
    <span class="d-chip__text">Chip</span>
  </button>
  <button class="d-chip__close" type="button" aria-label="close">
    <span class="d-btn__icon">
      <svg>...</svg>
    </span>
  </button>
</span>
'
vueCode='
<dt-chip>
  <template #avatar>
    <dt-avatar
      image-src="/assets/images/person.png"
      image-alt="Jaqueline Nackos"
      full-name="Jaqueline Nackos"
    />
  </template>
  <template #default>
    Chip
  </template>
</dt-chip>
'
showHtmlWarning />
<h3 id="truncated" tabindex="-1" class="d-docsite--header-3">Truncated <a class="header-anchor d-link d-docsite--link d-link" href="#truncated" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">To truncate text, add <code v-pre>.d-truncate</code> to the content element, and set the width of the <code v-pre>.d-chip</code> element.</p>
<code-well-header>
  <dt-chip content-class="d-w102">
    <span class="d-chip__text d-truncate">Chip loooooong name</span>
  </dt-chip>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-chip">
  <button class="d-chip__label d-w102" type="button">
    <span class="d-chip__text d-truncate">Chip loooooong name</span>
  </button>
  <button class="d-chip__close" type="button" aria-label="close">
    <span class="d-btn__icon">
      <svg>...</svg>
    </span>
  </button>
</span>
'
vueCode='
<dt-chip content-class="d-w102">
  <span class="d-chip__text d-truncate">Chip loooooong name</span>
</dt-chip>
'
showHtmlWarning />
<h3 id="sizes" tabindex="-1" class="d-docsite--header-3">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack direction="row" gap="400">
    <dt-chip :interactive="false" size="xs">Chip</dt-chip>
    <dt-chip :interactive="false" size="sm">Chip</dt-chip>
    <dt-chip :interactive="false">Chip</dt-chip>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-chip">
  <button class="d-chip__label d-chip__label--xs" type="button">
    <span class="d-chip__icon">
      <svg>...</svg>
    </span>
    <span class="d-chip__text">Chip</span>
  </button>
  <button class="d-chip__close d-chip__close--xs" type="button" aria-label="close">
    <span class="d-btn__icon">
      <svg>...</svg>
    </span>
  </button>
</span>
<span class="d-chip">
  <button class="d-chip__label d-chip__label--sm" type="button">
    <span class="d-avatar">...</span>
    <span class="d-chip__text">Chip</span>
  </button>
  <button class="d-chip__close d-chip__close--sm" type="button" aria-label="close">
    <span class="d-btn__icon">
      <svg>...</svg>
    </span>
  </button>
</span>
<span class="d-chip">
  <button class="d-chip__label" type="button">
    <span class="d-chip__icon">
      <svg>...</svg>
    </span>
    <span class="d-chip__text">Chip</span>
  </button>
  <button class="d-chip__close" type="button" aria-label="close">
    <span class="d-btn__icon">
      <svg>...</svg>
    </span>
  </button>
</span>
'
vueCode='
<dt-chip :interactive="false" size="xs">
  chip
</dt-chip>
<dt-chip :interactive="false" size="sm">
  chip
</dt-chip>
<dt-chip :interactive="false">
  chip
</dt-chip>
'
showHtmlWarning />
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="chip" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="chip" /></div></template>


